import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './AddToHomescreenPrompt.css';

const AddToHomescreenPrompt = () => {
  const { t, isRTL } = useLanguage();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem('addToHomescreenDismissed');
    if (dismissed) {
      return;
    }

    // Check if running in standalone mode (already installed)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');

    setIsStandalone(isStandaloneMode);

    if (isStandaloneMode) {
      return; // Already installed, don't show prompt
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (!isMobile) {
      return; // Not a mobile device
    }

    // For Android/Chrome: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay (e.g., 3 seconds after page load)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    // For iOS: Show prompt after delay
    if (iOS) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Also check if app was already installed (appinstalled event)
    const handleAppInstalled = () => {
      setShowPrompt(false);
      localStorage.setItem('addToHomescreenDismissed', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (showPrompt) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
      localStorage.setItem('addToHomescreenDismissed', 'true');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('addToHomescreenDismissed', 'true');
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleDismiss();
    }
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="add-to-homescreen-overlay" onClick={handleClickOutside}>
      <div
        ref={modalRef}
        className={`add-to-homescreen-modal ${isRTL ? 'rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="add-to-homescreen-header">
          <h3>{t('addToHomescreen.title')}</h3>
          <button
            type="button"
            className="add-to-homescreen-close-button"
            onClick={handleDismiss}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <div className="add-to-homescreen-content">
          <p>{t('addToHomescreen.description')}</p>

          {isIOS ? (
            <div className="add-to-homescreen-ios-instructions">
              <p className="ios-steps-title">{t('addToHomescreen.iosStepsTitle')}</p>
              <ol className="ios-steps-list">
                <li>{t('addToHomescreen.iosStep1')}</li>
                <li>{t('addToHomescreen.iosStep2')}</li>
                <li>{t('addToHomescreen.iosStep3')}</li>
              </ol>
            </div>
          ) : (
            <div className="add-to-homescreen-benefits">
              <ul>
                <li>{t('addToHomescreen.benefit1')}</li>
                <li>{t('addToHomescreen.benefit2')}</li>
                <li>{t('addToHomescreen.benefit3')}</li>
              </ul>
            </div>
          )}
        </div>

        <div className="add-to-homescreen-actions">
          {isIOS ? (
            <>
              <button
                className="add-to-homescreen-install-button"
                onClick={handleDismiss}
              >
                {t('addToHomescreen.gotItButton')}
              </button>
              <button
                className="add-to-homescreen-dismiss-button"
                onClick={handleDismiss}
              >
                {t('addToHomescreen.dismissButton')}
              </button>
            </>
          ) : (
            <>
              {deferredPrompt && (
                <button
                  className="add-to-homescreen-install-button"
                  onClick={handleInstall}
                >
                  {t('addToHomescreen.installButton')}
                </button>
              )}
              <button
                className="add-to-homescreen-dismiss-button"
                onClick={handleDismiss}
              >
                {t('addToHomescreen.dismissButton')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddToHomescreenPrompt;
